import type { ResolveInstrumentResponse } from "../types/instruments";
import type { AvailableInstrumentRow } from "../types/tradeManagementReport";
import {
  instrumentChartsBlocked,
  instrumentNeedsSetup,
} from "./instrumentMarketDataReadiness";
import { isInstrumentSelectableForContextReport } from "./reportInstrumentSelection";

/** Open instrument setup when Layer A setup is incomplete. */
export function shouldGuideInstrumentMarketDataSetup(
  row: AvailableInstrumentRow | null | undefined
): boolean {
  if (!row || row.md_supported === false) return false;
  return instrumentNeedsSetup(row);
}

/** Row has finished setup and lane can serve OHLC (Layers A + B). */
export function isInstrumentMarketDataReadyForCharts(
  row: AvailableInstrumentRow | null | undefined
): boolean {
  if (!row || !isInstrumentSelectableForContextReport(row)) return false;
  return !instrumentChartsBlocked(row);
}

export function availableInstrumentRowToResolveResult(
  row: AvailableInstrumentRow
): ResolveInstrumentResponse {
  return {
    symbol: row.symbol,
    instrument_id: row.instrument_id,
    asset_class: row.asset_class,
    market_data_broker: row.market_data_broker,
    market_data_symbol: row.market_data_symbol,
    market_data_source_label: row.market_data_source_label,
    market_data_broker_display: row.market_data_broker_display,
    md_listing_key: row.md_listing_key,
    md_supported: row.md_supported !== false,
    resolved: row.resolved ?? Boolean(row.md_listing_key?.trim()),
    setup_complete: row.setup_complete,
    chart_available: row.chart_available,
    blocking_reason: row.blocking_reason,
    chart_unavailable_reason: row.chart_unavailable_reason,
    requires_market_data_source_choice: row.requires_market_data_source_choice,
    requires_quote_variant_choice: row.requires_quote_variant_choice,
    quote_variant_overridable: row.quote_variant_overridable,
    auto_resolved_quote_variant: row.auto_resolved_quote_variant ?? undefined,
    quote_variant_candidates: row.quote_variant_candidates,
    suggested_quote_variant: row.suggested_quote_variant ?? undefined,
    selected_market_data_source: row.selected_market_data_source,
    market_data_sources: row.market_data_sources,
    candidate_brokers: row.candidate_brokers,
    ibkr_conid: row.ibkr_conid,
    ibkr_exchange: row.ibkr_exchange,
  };
}

/** Report wizard — block Continue when setup or lane blocks OHLC for market-context reports. */
export function computeGenerateMdBlocked(input: {
  selectedRow: AvailableInstrumentRow | undefined;
  resolveMdBlocked: boolean;
  resolveHasError: boolean;
  marketDataConnectGateBlocked?: boolean;
}): boolean {
  if (input.marketDataConnectGateBlocked) return true;
  const row = input.selectedRow;
  if (!row) return input.resolveMdBlocked || input.resolveHasError;
  if (row.md_supported === false) return true;
  if (isInstrumentMarketDataReadyForCharts(row)) return false;
  if (instrumentNeedsSetup(row)) return true;
  if (row.chart_available === false) return true;
  return input.resolveMdBlocked || input.resolveHasError;
}
